import React, { useContext, useState } from 'react';
import {
  CheckSquareOutlined,
  CloseSquareOutlined,
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  IdcardOutlined,
  PauseCircleOutlined,
  LoginOutlined,
} from '@ant-design/icons';
import { Button, Modal, Space, Tooltip, message } from 'antd';
import styled from '@emotion/styled';
import moment from 'moment-timezone';
import { useTranslation } from 'react-i18next';
import { ConfirmAction } from '../../../molecules/confirm/ConfirmAction';
import {
  postActivateTourTrash,
  postArchiveTourTrash,
  postCancelTourTrash,
  postCloseTourTrash,
  postDeleteTourTrash,
} from '../../../../repository/mytm/tour-trash/tourTrashRepository';
import { UserContext } from '../../../../repository/context/userContext';
import { postLoginRepository } from '../../../../repository/mytm/auth/loginRepository';
import { TOUR_TRASH_TRANSLATION } from '../../../../constants/define/translation';
import { TourTrashStatusType, TourTrashType } from '../../../../types/tourtrash';
import { MESSAGE_ERROR } from '../../../../constants/message';
import { btnActive } from '../../../../styles';
import { CONFIG_CONTAINER_NAME, deleteBlob } from '../../../../utils/azure';

interface ActionsColumnProps {
  record: TourTrashType;
  onRefresh: () => void;
  onHandleClickBtnEdit?: (record: TourTrashType, editTourTrashActive?: boolean) => void;
  onHandleViewTourtrash?: (record: TourTrashType) => void;
  setLoading?: (value: boolean) => void;
}

export default React.memo((props: ActionsColumnProps) => {
  const { record, onRefresh, onHandleClickBtnEdit, onHandleViewTourtrash, setLoading } = props;
  const { t } = useTranslation(TOUR_TRASH_TRANSLATION);
  const userContext = useContext(UserContext);

  const [deleting, setDeleting] = useState(false);
  const [activating, setActivating] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [closing, setClosing] = useState(false);

  const isActive = record.status === TourTrashStatusType.ACTIVE || record.status === TourTrashStatusType.COMING_SOON;
  const isDraft = record.status === TourTrashStatusType.DRAFT;
  const isClose = record.status === TourTrashStatusType.CLOSED;
  const isComingSoon = record.status === TourTrashStatusType.COMING_SOON;

  const canActivateTourTrash =
    record.endDate &&
    moment(record.endDate).isAfter(moment()) &&
    record.startDate &&
    moment(record.startDate).isAfter(moment());
  const canCloseTourTrash =
    (record.enteredUsers && record.winner && record?.winner.id) ||
    (!record.enteredUsers && record.endDate && moment(record.endDate).isBefore(moment()));

  const handleArchive = async () => {
    Modal.confirm({
      title: t('table.popup.title'),
      okText: t('table.popup.okBtn'),
      cancelText: t('table.popup.cancelBtn'),
      icon: <ExclamationCircleOutlined />,
      content: !isDraft ? (
        <ConfirmAction
          textConfirmAction={t('table.popup.archive')}
          isConfirmPassword
          textConfirmPassword={t('table.popup.confirmPassword')}
        />
      ) : (
        t('table.popup.archive')
      ),
      onOk() {
        setDeleting(true);
        const updateFunc = () => {
          postArchiveTourTrash({ id: record.id })
            .then(r => {
              setDeleting(false);
              onRefresh();
              message.success(t('message.tourTrash.archive'));
            })
            .catch(e => {
              setDeleting(false);
              message.error(t('message.tourTrash.archiveFail'));
            });
        };
        updateFunc();
      },
    });
  };

  const deleteImageTourTrash = async (images: any) => {
    if (!images || !images.length) return false;
    for (const value of images) {
      const urlImage = value?.imageUrl;
      deleteBlob(urlImage, CONFIG_CONTAINER_NAME.UPLOADS);
    }
    return true;
  };

  const handleDelete = async () => {
    Modal.confirm({
      title: t('table.popup.title'),
      okText: t('table.popup.okBtn'),
      cancelText: t('table.popup.cancelBtn'),
      icon: <ExclamationCircleOutlined />,
      content: !isDraft ? (
        <ConfirmAction
          textConfirmAction={t('table.popup.delete')}
          isConfirmPassword
          textConfirmPassword={t('table.popup.confirmPassword')}
        />
      ) : (
        t('table.popup.delete')
      ),
      onOk() {
        setDeleting(true);
        const updateFunc = () => {
          postDeleteTourTrash({ id: record.id })
            .then(r => {
              deleteImageTourTrash(record?.product?.images);
              setDeleting(false);
              onRefresh();
              message.success(t('message.tourTrash.delete'));
            })
            .catch(e => {
              setDeleting(false);
              message.error(t('message.tourTrash.deleteFail'));
            });
        };
        if (!isDraft) {
          const inputPasswordNode = document.getElementById('inputPasswordForConfirm');
          const password = (inputPasswordNode as any).value;
          if (!userContext.user?.email || userContext.user?.email === '') {
            setLoading && setLoading(false);
            message.error(MESSAGE_ERROR.M02);
          } else {
            postLoginRepository({
              email: userContext.user?.email,
              password,
            })
              .then(() => {
                updateFunc();
              })
              .catch((e: any) => {
                setDeleting(false);
                message.error(e?.errorMessage);
              });
          }
        } else {
          updateFunc();
        }
      },
    });
  };

  const handleActivate = async () => {
    Modal.confirm({
      title: t('table.popup.title'),
      okText: t('table.popup.okBtn'),
      cancelText: t('table.popup.cancelBtn'),
      icon: <ExclamationCircleOutlined />,
      content: (
        <ConfirmAction
          textConfirmAction={
            <>
              <p>{t('table.popup.activateContent')}</p>
              <span>{t('table.popup.activate')}</span>
            </>
          }
          isConfirmPassword
          textConfirmPassword={t('table.popup.confirmPassword')}
        />
      ),
      onOk() {
        setActivating(true);
        const inputPasswordNode = document.getElementById('inputPasswordForConfirm');
        const password = (inputPasswordNode as any).value;
        if (!userContext.user?.email || userContext.user?.email === '') {
          setLoading && setLoading(false);
          message.error(MESSAGE_ERROR.M02);
        } else {
          postLoginRepository({
            email: userContext.user?.email,
            password,
          })
            .then(() => {
              postActivateTourTrash({ id: record.id })
                .then(r => {
                  setActivating(false);
                  onRefresh();
                  message.success(t('message.tourTrash.activate'));
                })
                .catch(e => {
                  setActivating(false);
                  message.error(t('message.tourTrash.activateFail'));
                });
            })
            .catch((e: any) => {
              setActivating(false);
              message.error(e?.errorMessage);
            });
        }
      },
    });
  };

  const handleCancel = async () => {
    Modal.confirm({
      title: t('table.popup.title'),
      okText: t('table.popup.okBtn'),
      cancelText: t('table.popup.cancelBtn'),
      icon: <ExclamationCircleOutlined />,
      content: (
        <ConfirmAction
          textConfirmAction={t('table.popup.cancel')}
          isConfirmPassword
          textConfirmPassword={t('table.popup.confirmPassword')}
        />
      ),
      onOk() {
        setCanceling(true);
        const inputPasswordNode = document.getElementById('inputPasswordForConfirm');
        const password = (inputPasswordNode as any).value;
        if (!userContext.user?.email || userContext.user?.email === '') {
          setLoading && setLoading(false);
          message.error(MESSAGE_ERROR.M02);
        } else {
          postLoginRepository({
            email: userContext.user?.email,
            password,
          })
            .then(() => {
              postCancelTourTrash({ id: record.id })
                .then(r => {
                  setCanceling(false);
                  onRefresh();
                  message.success(t('message.tourTrash.cancel'));
                })
                .catch(e => {
                  setCanceling(false);
                  message.error(t('message.tourTrash.cancelFail'));
                });
            })
            .catch((e: any) => {
              setCanceling(false);
              message.error(e?.errorMessage);
            });
        }
      },
    });
  };

  const handleClose = async () => {
    Modal.confirm({
      title: t('table.popup.title'),
      okText: t('table.popup.okBtn'),
      cancelText: t('table.popup.cancelBtn'),
      icon: <ExclamationCircleOutlined />,
      content: (
        <ConfirmAction
          textConfirmAction={t('table.popup.close')}
          isConfirmPassword
          textConfirmPassword={t('table.popup.confirmPassword')}
        />
      ),
      onOk() {
        setClosing(true);
        const inputPasswordNode = document.getElementById('inputPasswordForConfirm');
        const password = (inputPasswordNode as any).value;
        if (!userContext.user?.email || userContext.user?.email === '') {
          setLoading && setLoading(false);
          message.error(MESSAGE_ERROR.M02);
        } else {
          postLoginRepository({
            email: userContext.user?.email,
            password,
          })
            .then(() => {
              postCloseTourTrash({ id: record.id })
                .then(r => {
                  setClosing(false);
                  onRefresh();
                  message.success(t('message.tourTrash.close'));
                })
                .catch(e => {
                  setClosing(false);
                  message.error(t('message.tourTrash.closeFail'));
                });
            })
            .catch((e: any) => {
              setClosing(false);
              message.error(e?.errorMessage);
            });
        }
      },
    });
  };

  const handleEdit = () => {
    if (isActive) {
      Modal.confirm({
        title: t('table.popup.title'),
        okText: t('table.popup.okBtn'),
        cancelText: t('table.popup.cancelBtn'),
        icon: <ExclamationCircleOutlined />,
        content: (
          <ConfirmAction
            textConfirmAction={t('table.popup.edit')}
            isConfirmPassword
            textConfirmPassword={t('table.popup.confirmPassword')}
          />
        ),
        onOk() {
          setLoading && setLoading(true);
          const inputPasswordNode = document.getElementById('inputPasswordForConfirm');
          const password = (inputPasswordNode as any).value;
          if (!userContext.user?.email || userContext.user?.email === '') {
            setLoading && setLoading(false);
            message.error(MESSAGE_ERROR.M02);
          } else {
            postLoginRepository({
              email: userContext.user?.email,
              password,
            })
              .then(() => {
                setLoading && setLoading(false);
                if (onHandleClickBtnEdit) {
                  onHandleClickBtnEdit(record);
                }
              })
              .catch((e: any) => {
                setLoading && setLoading(false);
                message.error(e?.errorMessage);
              });
          }
        },
        onCancel() {
          // NOT
        },
      });
    } else if (onHandleClickBtnEdit) {
      onHandleClickBtnEdit(record);
    }
  };

  if (isDraft) {
    return (
      <StyledContainer>
        <Space size="small">
          {canActivateTourTrash && (
            <Tooltip title={t('table.tooltip.active')}>
              <Button
                className="active"
                disabled={activating}
                loading={activating}
                icon={<CheckSquareOutlined />}
                onClick={handleActivate}
              />
            </Tooltip>
          )}
          <Tooltip title={t('table.tooltip.edit')}>
            <Button icon={<EditOutlined />} onClick={handleEdit} />
          </Tooltip>
          <Tooltip title={t('table.tooltip.delete')}>
            <Button
              className="delete"
              icon={<DeleteOutlined />}
              disabled={deleting}
              loading={deleting}
              onClick={handleDelete}
            />
          </Tooltip>
        </Space>
      </StyledContainer>
    );
  }
  return (
    <StyledContainer>
      <Space size="small">
        {isComingSoon && (
          <Tooltip title={t('table.tooltip.edit')}>
            <Button icon={<EditOutlined />} onClick={handleEdit} />
          </Tooltip>
        )}
        {isActive && (
          <Tooltip title={t('table.tooltip.cancel')}>
            <Button loading={canceling} disabled={canceling} icon={<PauseCircleOutlined />} onClick={handleCancel} />
          </Tooltip>
        )}
        {isActive && canCloseTourTrash && (
          <Tooltip title={t('table.tooltip.close')}>
            <Button loading={closing} disabled={closing} icon={<CloseSquareOutlined />} onClick={handleClose} />
          </Tooltip>
        )}
        <Tooltip title={t('table.tooltip.view')}>
          <Button
            onClick={() => {
              if (onHandleViewTourtrash) onHandleViewTourtrash(record);
            }}
            icon={<IdcardOutlined />}
          />
        </Tooltip>
        {isClose && (
          <Tooltip title={t('table.tooltip.archive')}>
            <Button
              className="archive"
              icon={<LoginOutlined />}
              disabled={deleting}
              loading={deleting}
              onClick={handleArchive}
            />
          </Tooltip>
        )}
        <Tooltip title={t('table.tooltip.delete')}>
          <Button
            className="delete"
            icon={<DeleteOutlined />}
            disabled={deleting}
            loading={deleting}
            onClick={handleDelete}
          />
        </Tooltip>
      </Space>
    </StyledContainer>
  );
});

const StyledContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-right: 0.5rem;
  ${btnActive}
`;
